import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatPaginator, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { tap } from "rxjs";
import { GrilleTri } from "src/app/zzz/ui/grille/grille.modele";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { RechercheStatistique, Statistique } from "../pilotage.models";
import { PilotageService } from "../pilotage.service";
import { FormService } from "src/app/shared/services/form.service";
import { ValeurDomaineDto } from "src/app/shared/models/domaines.dto";
import { DomainesService } from "src/app/shared/services/domaines.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker';
import { MessageService } from "@igo2/core/message";
import { LanguageService } from "@igo2/core/language";
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";

interface RechercheDto {
  dateDebut?: Date;
  dateFin?: Date;
  type?: string;
}

interface TableState {
  sort?: GrilleTri;
  page: number;
  pageSize: number;
  pageLength: number;
  recherche?: RechercheDto;
}

@Component({
    selector: 'pilotage-statistiques',
    templateUrl: './pilotage-statistiques.component.html',
    styleUrls: ['./pilotage-statistiques.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatNativeDateModule,
        MatDatepickerModule,
        MatDatepickerToggle,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        TranslateModule,
        MatSortModule
    ]
})
export class PilotageStatistiquesComponent implements OnInit {
  #formBuilder = inject(FormBuilder);
  #formService = inject(FormService);
  #pilotageService = inject(PilotageService);
  #messageService = inject(MessageService);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  form = this.#formBuilder.group({
    dateDebut: [null],
    dateFin: [null],
    type: [null]
  }, {
    validators: [this.validerUnChampRempli.bind(this), this.validerDateDebutFin.bind(this)]
  });

  displayedColumns: string[] = ['codeUtilisateur', 'nomPrenom', 'type', 'nomDirectionOuEmployeur', 'nombreAcces'];
  defaultValue: Statistique[] = [];
  dataSource = new MatTableDataSource<Statistique>(this.defaultValue);
  tableState: TableState = {
    page: 1,
    pageSize: 25,
    pageLength: 0
  };
  listeTypeUtilisateur: ValeurDomaineDto[];
  messagesDeValidation: { [key: string]: string } = {};

  constructor(
    private languageService: LanguageService,
    private domaineService: DomainesService
  ) { }

  ngOnInit(): void {
    this.domaineService
      .obtenirValeurs("TYPE_UTILISATEUR")
      .subscribe({
        next: (data) => {
          this.listeTypeUtilisateur = data.valeursDomaine;
        },
        error: (err) => {
          console.error(err);
        },
      });

    this.messagesDeValidation.requiredAtLeastOne =
      this.languageService.translate.instant('messages.requiredAtLeastOne')
        .replace('{0}', this.languageService.translate.instant('journal.statistiqueConnexion.form.messagesPlaceholders.ph1UnParametre'));
    this.messagesDeValidation.dateDebutInferieurEgaleDateDuJour =
      this.languageService.translate.instant('messages.dateDebutInferieurEgaleDateDuJour');
    this.messagesDeValidation.dateDebutSuperieurFin =
      this.languageService.translate.instant('messages.dateDebutSuperieurFin');
  }

  reinitialiser(): void {
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.tableState.recherche = undefined;
    this.tableState.page = 1;
    this.tableState.pageSize = 25;
    this.tableState.pageLength = 0;
    this.dataSource.data = this.defaultValue;
  }

  rechercher(): void {
    this.form.markAsTouched();

    if (this.#formService.valider(this.form)) {
      this.tableState.recherche = this.form.value;
      this.tableState.page = 1;
      this.#chargerDonnees();
    }
  }

  #chargerDonnees(): void {
    var data: RechercheStatistique = {
      dateDebut: this.tableState.recherche.dateDebut,
      dateFin: this.tableState.recherche.dateFin,
      type: this.tableState.recherche.type,
      criteres: {
        tris: this.tableState.sort && this.tableState.sort.order ? [
          new GrilleTri({ colonne: this.tableState.sort.colonne, order: this.tableState.sort.order })
        ] : [],
        skip: this.tableState.pageSize * this.tableState.page,
        page: this.tableState.page,
        take: this.tableState.pageSize,
        sort: null,
        count: 0,
        filtres: [],
        pages: null,
        recherche: null
      }
    };

    this.#pilotageService.obtenirStatistiquesGrille(data).pipe(
      tap(rep => {
        this.tableState = { ...this.tableState, ...{ pageLength: rep.criteres.count } };
        rep.data.forEach(
          (f) =>
          (f.type = this.languageService.translate.instant(
            `journal.statistiqueConnexion.grille.transformation.type.${f.type}`
          ))
        );
        this.dataSource.data = rep.data;

        if (this.dataSource.data.length === 0) {
          this.#messageService.message({
            text: this.languageService.translate.instant('sigt.global.messageAucuneDonnee')
          });
        }
      })
    ).subscribe();
  }

  trier(event: Sort): void {
    if (event && event.direction && (this.tableState.sort === undefined || this.tableState.sort.order !== event.direction)) {
      this.tableState.sort = {
        order: event.direction,
        colonne: event.active
      };
    } else if (event === undefined || event.direction === '') {
      this.tableState.sort = undefined;
    }

    if (this.tableState.recherche) {
      this.#chargerDonnees();
    }
  }

  changerPage(event: PageEvent): void {
    this.tableState.page = event.pageIndex + 1;
    this.tableState.pageSize = event.pageSize;

    if (this.tableState.recherche) {
      this.#chargerDonnees();
    }
  }

  validerUnChampRempli(controlGroup: FormGroup): { [key: string]: any } | null {
    const hasValue = controlGroup.controls["dateDebut"].value || controlGroup.controls["type"].value ? true : false;
    return hasValue ? null : {
      requiredAtLeastOne: {
        message: this.messagesDeValidation?.requiredAtLeastOne
      }
    };
  }

  validerDateDebutFin(controlGroup: FormGroup): { [key: string]: any } | null {
    const champDateDebut = controlGroup.controls["dateDebut"].value;
    const champDateFin = controlGroup.controls["dateFin"].value;
    if (champDateDebut) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateDebut = new Date(champDateDebut);
      dateDebut.setHours(0, 0, 0, 0);

      if (dateDebut > today)
        return {
          dateDebutFinErreur:
          {
            message:
              this.messagesDeValidation.dateDebutInferieurEgaleDateDuJour.replace('{0}',
                this.languageService.translate.instant
                  ('journal.statistiqueConnexion.form.messagesPlaceholders.ph0DateDebutInferieurEgaleDateDuJour'))
          }
        };

      if (champDateFin) {
        const dateFin = new Date(champDateFin);
        dateFin.setHours(0, 0, 0, 0);

        if (dateDebut > dateFin)
          return {
            dateDebutFinErreur: {
              message: this.messagesDeValidation.dateDebutSuperieurFin.replace('{0}', '')
            }
          };
      }
    }
    return null;
  }
}
